import { AfterViewInit, Directive, OnDestroy } from '@angular/core';
import { IFreedataWidget, IWidget, IWidgetFilter } from "../../../manager/widgets/i-widget";
import { WidgetBasic } from './widget-basic';
import { AkmFDVCell, BaselineEngine, EM_ACTION, FeiKeyDef, FIELD_KIND, KEY_KIND, VisionVO, WizConstants } from '@wizeoo/akm-compute-lib';
import { DataPack, EntityModificationTrace, FreeData, VisionFilterKey, VisionKey, VisionStep, WidgetConfigurationItemObj, WidgetConfigurationObj, WidgetSelect } from '@wizeoo/akm-message-lib';
import { ALK_EVENT_TYPE, WIDGET_ACTION_ORIGIN, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { ConfirmationResultObj, WizDatasource, WizPopUp, WizRoot, WizEvent, WizEventManager, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { CONFIRMATION_MODE, CONFIRMATION_RESULT, WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import { FreedataValueVO } from  '../../../vo/core/freedatavalue.vo';
import { AlkEvent } from '../../../framework/events/alk.event';
import { isEqual } from 'lodash-es';
import { UpdateEntry, CompositeSourcePack } from '../../../command/core/sync/sync.service';
import { FreeDataVO } from '../../../vo/core/freedata.vo';
import { getDefaultWidgetConfigurationObj } from '../../../manager/widgets/widget.engine';
import { ViewData } from '../../../model/view.data';
import { FreedataArgsVO } from '../../../vo/core/freedataargs.vo';

@Directive()
export abstract class FreedataWidgetBasic extends WidgetBasic implements IWidget, AfterViewInit, OnDestroy {

  public fdvDS: WizDatasource<FreedataValueVO> = new WizDatasource<FreedataValueVO>([]) // DS de la liste
    .setSortFields([{ property: 'createTime', way: 'desc', numeric: true }])
    .setSelectionMode('multiple')
    .setFilter(this.fdvFilterFunction.bind(this))
    .build();

  public freedataCode: string = null; // mémoriser le code du FD pour comparaisons rapides
  public level: number = null; // le niveau hiérarchique des FDVs manipulés
  public selectedKeys: Set<string> = null; // les éléments sélectionnés, juste leur code. Pour filtre rapide des slaves
  public fkd: FeiKeyDef = null; // la clé pour ce widget, Key ou FilterKey

  /**
   * @override pour passage à la suite
   * @param v 
   * @param vs 
   * @param scenario 
   */
  public setDatasource(v: VisionVO, vs: VisionStep, scenario: string): void {
    this.vision = v;
    if(vs) {
      // console.log('linkedVS');
      scenario = WizConstants.BASE_SCENARIO;
      super.setDatasource(v, vs, scenario, getDefaultWidgetConfigurationObj());
    } else {
      this.state = WIDGET_STATE.FEI_COMPUTED; // alimentation de l'état
      this.drawContent();
    }

  }

  /**
   * construction de la FeiKeyDef du Widget Freedata
   * @param vision 
   * @param context 
   * @returns la FeiKeyDef
   */
  public buildFeiKeyDef(vision: VisionVO, context: WidgetConfigurationObj): FeiKeyDef {
    let key: FeiKeyDef = null;
    // une clé de vision
    Object.values(this.vision.visionKeys).forEach((vk: VisionKey) => {
      if(vk.code === this.context.fdk) {
        key = BaselineEngine.buildFeiKeyDef(this.fei, KEY_KIND.KEY, vk);
      }
    });
    // ou une filter
    if(!key) {
      Object.values(this.vision.visionFilterKeys || {}).forEach((vfk: VisionFilterKey) => {
        if(vfk.code === this.context.fdk) {
          key = BaselineEngine.buildFeiKeyDef(this.fei, KEY_KIND.FILTER_KEY, vfk);
        }
      });
    }

    // ou rien... si j'édite un subFD, par exemple les livrets chez Giphar
    if(!key) {

      // on crée un fake de visionFilterKey
      let parts: string[] = this.context.fdk.split(WizConstants.CONTEXT_SEPARATOR);
      let fakeVFK = {chronoCode: null, code: this.context.fdk, level: Number(parts[1]), levelCode: 'BOB', order: 0, visionCode: vision.code};
      key = BaselineEngine.buildFeiKeyDef(this.fei, KEY_KIND.FILTER_KEY, fakeVFK as VisionFilterKey);
    }

    key.sortable = true; // tri toujours actif sur FDGrid
    key.editable = true; // LMA ajout de l'édition sur les FDGrid
    this.fkd = key;
    return key;
  }

  /**
   * construction du pool de lignes
   */
  public async buildLines(): Promise<void> {
    if(!!this.context.fdk) {
     
      // par défaut, tous les FDVs du Freedata
      let basePool: FreedataValueVO[] = this.model.freedataEngine.getFreedataValuesByLevel(this.context.fdk);
      
      // réduction du périmètre par le master
      let master: IWidget = this.model.widgetEngine.checkFirstMaster(this);
      if(!!master) {
        basePool = this.model.widgetEngine.availableFreedataMasterLines(this, master as IFreedataWidget, this.context.fdk, basePool);
      }
      // console.log('fdwidget base', basePool.length);
      // calcul des champs formules
      this.model.freedataEngine.computeFormulasOnFDV(this.freedataCode, basePool);
      
      this.fdvDS.setSource(basePool);
    }
  }

  /**
   * @override
   */
  public buildFilters(): void {
    // console.log('wb buildFilters');
    this.filters = null; //reset

    if(!!this.context.f) {
      this.filters = new Map();
      this.context.f.forEach((wcio: WidgetConfigurationItemObj) => { 
        let filter: IWidgetFilter = this.model.widgetEngine.buildFreedataFilter(this, wcio);
        let key: string = filter.fkd.fullCode + filter.wcio.fdvf;
        if(filter.wcio.cod === ViewData.FILTER_GENERIC) { // clé du filtre générique
          key = ViewData.FILTER_GENERIC + filter.wcio.fdvf;
        }
        this.filters.set(key, filter);
      });
    } 
  }

  /** présence d'un filtre générique pour génération des chaînes de texte */
  public hasGenericFilter(): boolean {
    let res = false;
    let filter: IWidgetFilter
    if(this.filters) { 
      for (var i = 0, len = this.filters.size; i < len; i++) {
        filter = [...this.filters.values()][i];      
        // filtre générique, on doit construire la chaine de valeur si elle n'existe pas
        if (filter.wcio.cod === ViewData.FILTER_GENERIC) {
          res = true;
          break;
        }
      }
    }
    
    return res;
  }


  /**
   * FilterFunction du grid, sur la base des filtres
   * @param item WidgetLine
   * @returns true si visible
   */
  public fdvFilterFunction(item: FreedataValueVO): boolean {
    let result: boolean = true; // on part du principe que çà passe
    let filter: IWidgetFilter; // les filtres qu'on va jouer

    // contrôle par filtre utilisateur
    if(!this.context.noUserFilter) {
      //console.log('fdvFilterFunction', this.context.noUserFilter);
      result = this.model.widgetEngine.doUserFilterOnFDV(item);  
    }
    

    // fin du contrôle par utilisateur, première chance de rater
    if(!result) { // le filtre global me laisse passer
      // item.isRendered = false;
      return false; // filtré par le filtre global
    }  

    // contrôle par filtre global
    if(!!this.model.widgetEngine.globalFilter) { 
      let globalFilter: IWidget = this.model.widgetEngine.checkGlobalFilter();
      if(globalFilter.filters) { // filtre actifs sur le global
        for (var i = 0, len = globalFilter.filters.size; i < len; i++) {
          filter = [...globalFilter.filters.values()][i];
          
          if(filter.mandatory && !filter.active) { // filtre global obligatoire sans valeur
            result = false;
            // console.log('oups mandatory global FDV void', filter);
            break;
          }
          
          
          if (filter.active) {
            // TODO tester la propriété sur le FD.arg
            try {

              // filtre générique, on doit construire la chaine de valeur si elle n'existe pas
              if (filter.wcio.cod === ViewData.FILTER_GENERIC) {
                if (!item.byWidgetStringMap?.has(this.uid)) {
                    this.feedFdvAsString(item); // slow path, mais ne devrait presque jamais arriver
                }
                const allFound = [...filter.values].every(v => item.byWidgetStringMap.get(this.uid).includes(v.toUpperCase()));
                if(allFound) {
                  // OK
                } else {
                  result = false;
                }
              }
              
               else if(filter.wcio.cod === ViewData.FILTER_ME) {
                if(item.argsObjVO[ViewData.FIELD_CREATE_BY] === this.model.currentUser.mail ||
                  item.argsObjVO[ViewData.FIELD_UPDATE_BY] === this.model.currentUser.mail) {
                    // OK
                  } else {
                    result= false;
                  }
              } 

              else {

                let arg: FreedataArgsVO = item.getParentFDVO()?.getArgsVOMap()?.get(filter.fkd.code);
                if(arg) {
                  let fdvVal: string = item.argsObjVO[arg.code]?.split(WizConstants.CONTEXT_SEPARATOR)[1] || null;
                  if(filter.regex) {
                    // TODO check
                    console.log('regEx', filter.regex);
                    result = this.model.widgetEngine.doFreedataFilter(item, filter, this.uid);
                  } else {
                    if(!filter.values.has(fdvVal) ) {
                      result = false;
                      break;
                    }
                  }
                  
                }
              }
            } catch(e:any) {
              console.log('oups', item.freeDataCode, item.code, e);
            }

          }    
        }
      }
    }

    // fin du contrôle global, première chance de rater
    if(!result) { // le filtre global me laisse passer
      // item.isRendered = false;
      return false; // filtré par le filtre global
    }


    if(this.filters) { 
      for (var i = 0, len = this.filters.size; i < len; i++) {
        filter = [...this.filters.values()][i];
        if (filter.active) {
          
          // filtre générique, on doit construire la chaine de valeur si elle n'existe pas
          if (filter.wcio.cod === ViewData.FILTER_GENERIC) {
            if (!item.byWidgetStringMap?.has(this.uid)) {
                this.feedFdvAsString(item); // slow path, mais ne devrait presque jamais arriver
            }
          }
               
          result = this.model.widgetEngine.doFreedataFilter(item, filter, this.uid);  
          if(!result) {
            break;
          }
        }
      }
    }

    return result;
  }

  /** construction des rendus sous forme de string pour l'ensemble des champs */
  public feedFdvAsString(fdv: FreedataValueVO): void {
    if(!fdv.byWidgetStringMap) {
      fdv.byWidgetStringMap = new Map();
    } else {
      if(fdv.byWidgetStringMap.has(this.uid)){
        // console.log('exist', fdv.byWidgetStringMap);
        return;
      }

    }

    let res: string[] = [];
    let src: WidgetConfigurationItemObj[] = null;
    if(this.widgetKind === WIDGET_KIND.FD_GRID) {
      src = [...this.context.r];
    } else { // tiles
      src = [...this.context.ff];
      this.context.f?.forEach(f => {
        if(f.cod !== ViewData.FILTER_GENERIC && f.cod !== ViewData.FILTER_ME) {
          src.push(f);
        }
      });
    }

    [...src].forEach(wcio => {

        // champ simple, ou référence d'un autre FD
        const field : string = wcio.fdvf.split(WizConstants.FD_PARTS_SEPARATOR)[0];
        let result: any = fdv.argsObjVO[field];
        let conf: FreedataArgsVO = this.model.freedataEngine.getArgFromFD(fdv.freeDataCode, field);
        
        // pas les types non lisibles
        if( !!conf && conf.dataType !== FIELD_KIND.BOOLEAN &&
            conf.dataType !== FIELD_KIND.BINARY &&
            conf.dataType !== FIELD_KIND.BINARY_MULTI
        ) {

          if(!!result) {
            if (conf?.dataType === FIELD_KIND.OBJECT) { // sous-FDV
              let parts: string[];
              try {
                parts = result.split(WizConstants.CONTEXT_SEPARATOR);
              } catch(e: any) {
                // console.log('bad result', result, e);
                parts = [];
                parts.push['oups error'];
              }
              
              let realFD: FreeDataVO = this.model.freedataEngine.getFreeData(parts[0]);
              let subfdv = realFD?.getFreeDataValue(parts[1]);
              if(subfdv) {
                let subField: string = wcio.subField || ViewData.FIELD_LABEL;
                conf = this.model.freedataEngine.getArgFromFD(realFD.code, subField);
                // console.log('subField', subField);
                if( !!conf && conf.dataType !== FIELD_KIND.BOOLEAN &&
                    conf.dataType !== FIELD_KIND.BINARY
                ) {
                  result = subfdv?.argsObjVO[subField];
                }
                // 
                
              } else {
                result = '';
              }
            }
            if(!!result) {
              try {
                res.push(result.toString().toUpperCase());
              } catch(e: any) {
                console.log('malformed result', result);
              }
             
            }
          }
        }
    });

    fdv.byWidgetStringMap.set(this.uid, res.join(WizConstants.KEY_SEPARATOR));
    // console.log('feedFdvAsString', fdv.code, res.join(WizConstants.KEY_SEPARATOR));
  }


  /* ##############################################################################
                gestion du contexte
  ############################################################################## */

  /**
   * changement de mon pool de sélection... intercepté sur AgGrid
   * on va informer les éventuels esclaves.
   * @param e 
   */
  public innerSelectionChange(e: WizEvent): void {
    if(this.state < WIDGET_STATE.READY) { // pas encore affiché, inutile
      return;
    }
    this.onSelectionChanged(); // persistence du contexte de sélection
    this.updateComponentSelection(); // informer le monde que le master (éventuel) a changé

  }

  /** mémorisation du contexte de sélection */
  public onSelectionChanged(persist: boolean = true): void {
    if(!this._context) { // inutile si le contexte n'est pas instancié (incexc, vsp)
      return; 
    }
    const selMap: Map<string, WidgetSelect> = new Map();
    this.fdvDS.selectedItems?.forEach((fdv: FreedataValueVO) => {
      selMap.set(fdv.code, {k: fdv.code, v: null});
    });

    const context: WidgetConfigurationObj = this._context;
    
    const selKey: any[] = [];
    selMap.forEach((val: any, key: string) => {
      selKey.push(val);
    });


    let hasUpdate: boolean = !isEqual(context.sel, selKey);
    // console.log('fdGrid has update', context.sel, selKey);
    context.sel = selKey; // selKey.join(ViewData.KEY_SEPARATOR);
    if (persist) {
      this.updateContext(context, hasUpdate);
    }
  }

  /**
   * sur alimentation des rows, définition du pool sélectionné depuis les valeurs du contexte
   */
  public feedSelectionFromContext(context: WidgetConfigurationObj): void {
    const selectPool: FreedataValueVO[] = [];
    // console.log('feedSelectionFromContext 0', this, this.state, this.fdvDS, context.sel);
      
    if (context.sel?.length > 0) { 
      // pas de sélection mémorisée, rien à faire

      // TODO contrôle de cohérence des sélections obsolètes ?
      const selectionKeyMap: Map<string, WidgetSelect> = new Map(context.sel.map((obj: WidgetSelect) => [obj.k, obj]));

      this.fdvDS.getWorkedSource().forEach((fdv: FreedataValueVO) => {
        if (selectionKeyMap.has(fdv.code)) {
          selectPool.push(fdv);
        }
      });
      
      // this.fdvDS.select(selectPool);
      setTimeout(() => {
        this.fdvDS.select(selectPool);
        this.fdvDS.refresh();
        this.state = WIDGET_STATE.SELECTION_COMPLETE; // et on passe à l'état sélectionné
        // console.log('feedSelectionFromContext select', this, this.state, this.fdvDS.selectedItems, selectPool);
      
        WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_UPDATED, this)); 
      });
    } else {
      
      this.state = WIDGET_STATE.SELECTION_COMPLETE; // et on passe à l'état sélectionné
      // console.log('feedSelectionFromContext no select', this, this.state);
      // pas de sélection, on dispatch tout de suite
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_UPDATED, this)); 
    }
  }


/* ##############################################################################
                cycle de vie
    ############################################################################## */

    ngAfterViewInit() {
    super.ngAfterViewInit();
    // retour de service de commit, possible impact pour mon widget
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.FREEDATA_UPDATED, this.checkItemAlter);
    WizEventManager.addListener(this, WizRoot.root(), WIZ_EVENT_TYPE.LOCALE_CHANGED, this.localizeMe);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.FREEDATA_UPDATED);
    WizEventManager.removeListener(this, WizRoot.root(), WIZ_EVENT_TYPE.LOCALE_CHANGED);
  }

  /**
   * modification des FD / FDVs
   * @param e, data = DataPack, issu du retour de commit
   */
  private checkItemAlter(e:AlkEvent): void { 

    // if(this.widgetKind === WIDGET_KIND.FD_FORM) {
    //   console.log('checkItemAlter', e.data);
    // }

    // console.log('checkItemAlter', e.data);
    const updates: DataPack = e.data;
    let myConcern: boolean = false; // c'est mon FD
    if(updates.inFreedata?.code === this.freedataCode) {
      // tester le niveau des FDV
      updates.inFreedataValues?.forEach((fdv) => {
        if(fdv.level <= this.level) {
          myConcern = true;
        }
      })  
    }

    if(!myConcern) {
      // modifs par un autre
      updates.remoteFreedata?.forEach((fd: FreeData) => {
        if(fd.code === this.freedataCode) {
          updates.remoteFreedataValues?.forEach((fdv) => {
            if(fdv.level <= this.level) {
              myConcern = true;
            }
          })
        }
      })
    }

    if(!myConcern) {
      // éléments supprimés ?
      updates.deletedFreedataAndValues?.forEach((emt: EntityModificationTrace) => {
        if(emt.key === this.freedataCode && emt.l === this.level) {
          myConcern = true;
        }
      })
    }

    if(myConcern) {
      // console.log('checkItemAlter myConcern', e.data);
      // TODO TODO faire moins bourrin
      this.alterContent(WIDGET_ACTION_ORIGIN.RECOMPUTE, updates); // le drawContent va dispatch un event pour les esclaves...
    }
  }

  /**
   * demande de suppression en masse pour les widgets de type Freedata
   * pour override dans FreedataWidgetBasic
   * @param e 
   * @override
   */
  public massDeleteRequest(e: AlkEvent): void {
    // console.log('massDeleteRequest', this, this.fdvDS?.getWorkedSource()?.length, this.fdvDS?.selectedItems?.length);
    if(!this.context.shs) { // pas de colonne de sélection, on supprime TOUT
      let howMany: string = WizTranslationManager.getInstance().translateKey('$.mass.delete.detail.$', [this.fdvDS.getSource().length.toString()]);
      WizPopUp.confirm(
        '$.pivot.delete.lines.title.$',
        howMany,
        this.massDeleteConfirm.bind(this),
        CONFIRMATION_MODE.CONFIRM_CANCEL,
        this.fdvDS.getSource()
      ).show();
    } else if(this.fdvDS?.selectedItems?.length > 0) {
      /** demande de suppression */
      let howMany: string = WizTranslationManager.getInstance().translateKey('$.mass.delete.detail.$', [this.fdvDS.selectedItems.length.toString()]);
      WizPopUp.confirm(
        '$.pivot.delete.lines.title.$',
        howMany,
        this.massDeleteConfirm.bind(this),
        CONFIRMATION_MODE.CONFIRM_CANCEL,
        this.fdvDS.selectedItems
      ).show();
    }
  }


  /**
   * confirmation de la suppression en masse
   * @param result Objet de retour sur popUp de confirmation. Contient l'action choisie, et la data d'origine
   */
  private massDeleteConfirm(result: ConfirmationResultObj): void {
    if (result.result === CONFIRMATION_RESULT.CONFIRM) {
      this.doDelete(result.data);
    }
  }

  /**
   * confirmation de la suppression, on joue les delete
   * @param todie 
   */
  public doDelete(todie: FreedataValueVO[]): void {

    // console.log('doDelete', todie);
    let witness: FreedataValueVO = todie[0];

    const toDieClone: FreeDataVO = this.model.freedataEngine.cloneFD(witness.getParentFDVO());
    
    let updates: UpdateEntry[] = null;
    todie.forEach(fdv => {
      toDieClone.addToUpdatePack(fdv, EM_ACTION.DELETE); // marquer la suppression du FDV
      this.model.freedataEngine.deleteFDV(toDieClone, fdv);  // altération du clone pour supprimer son FDV
      
      if(!!this.fei) { // step asservi
        updates = this.model.freedataEngine.generateSlaveBaselines(toDieClone, this);
      }
    });
    
    const csp: CompositeSourcePack = {
      freedata: toDieClone, // update / create / delete sur FDV
      freedataManagement: true,
      updates, // maj sur des Baselines / BLVs
    }
    this.model.compositeEngine.compositeCommit(csp)
  }

  /** fin de rechargement de la trad */
  private localizeMe(e: WizEvent): void {
    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.RECOMPUTE);  
  }

}
